import * as React from 'react'
import { Link } from 'react-router-dom'
import { graphHighlightHref } from '@atbash-labs/plantview-links'
import type { NodeLabel } from '@atbash-labs/ontology-core'
import {
  collectAdditionalProperties,
  useGetPlantUtilityDetailQuery,
  usePlantSessionPaint,
  type PlantLine,
  type PlantSite,
  type PlantUtilityConsumer,
  type PlantUtilityDetailNode
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { cn } from '../../../lib/utils'
import { plantEquipmentIndexHref } from '../../../lib/plant-links'
import { PlantHref } from '../PlantHref'
import { registryIconFor } from '../registry-icons'
import {
  SessionBreadcrumbCurrent,
  SessionEntityMark,
  SessionTitleMark,
  sessionDetailHeaderClass
} from '../session-mark-ui'
import { basisStatusClass, formatUtilitySpec } from '../utility-format'
import { EntityDetailsInspector } from './EntityDetailsInspector'
import { PlantStrayPill } from './PlantStrayPill'
import { SectionHeader } from './SectionHeader'

export function UtilityEntityPage({
  entityId
}: {
  entityId: string
  label: NodeLabel
}): React.ReactElement {
  const { data, isLoading, isError } = useGetPlantUtilityDetailQuery(entityId, {
    skip: !entityId
  })
  const { markFor } = usePlantSessionPaint()

  if (isLoading) {
    return (
      <div className="flex min-w-0 flex-col gap-card">
        <UtilityFactoryBreadcrumb />
        <div className="rounded-xl border bg-card p-8 text-sm text-ink-3">Loading utility…</div>
      </div>
    )
  }
  if (isError || !data) {
    return (
      <div className="flex min-w-0 flex-col gap-card">
        <UtilityFactoryBreadcrumb />
        <div className="rounded-xl border bg-card p-8 text-card-foreground shadow-sm">
          <h1 className="m-0 text-xl font-semibold">Utility not found</h1>
          <p className="mt-2 break-all font-mono text-xs text-ink-3">{entityId}</p>
          <Button asChild variant="outline" className="mt-5">
            <Link to={graphHighlightHref(entityId)}>Open graph view</Link>
          </Button>
        </div>
      </div>
    )
  }

  const { utility, consumers, site, line } = data
  const additionalProperties = collectAdditionalProperties(
    'Utility',
    utility as unknown as Record<string, unknown>
  )
  const Icon = registryIconFor('Utility')
  const kindChips = kindSpecificChips(utility)
  const utilityMark = markFor(utility.id)

  return (
    <div className="flex min-w-0 flex-col gap-card">
      <UtilityFactoryBreadcrumb
        site={site}
        line={line}
        currentId={utility.id}
        currentName={utility.name}
      />

      <header
        className={sessionDetailHeaderClass(
          'flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4',
          utilityMark
        )}
      >
        <span className="grid size-12 shrink-0 place-items-center rounded-xl bg-brand-soft text-brand">
          <Icon size={24} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="m-0 min-w-0 break-words text-2xl font-semibold text-ink">
              {utility.name}
            </h1>
            <SessionTitleMark mark={utilityMark} />
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <Badge variant="outline">Utility</Badge>
            {utility.utility_kind && <Badge variant="secondary">{utility.utility_kind}</Badge>}
            {kindChips.map((chip) => (
              <Badge key={chip} variant="outline">
                {chip}
              </Badge>
            ))}
            <PlantStrayPill isStray={data.isStray} />
          </div>
          <div className="mt-1.5 break-all font-mono text-11 text-ink-4">{utility.id}</div>
        </div>
      </header>

      {utility.description && (
        <p className="m-0 text-14 leading-relaxed text-ink-2">{utility.description}</p>
      )}

      <ConsumersSection consumers={consumers} />

      <EntityDetailsInspector entity={utility} additionalProperties={additionalProperties} />
    </div>
  )
}

function kindSpecificChips(utility: PlantUtilityDetailNode): string[] {
  const chips: string[] = []
  if (utility.voltage_class) chips.push(utility.voltage_class)
  if (utility.water_kind) chips.push(utility.water_kind)
  if (utility.refrigerant_type) chips.push(utility.refrigerant_type)
  if (utility.utility_kind === 'other' && utility.utility_kind_other) {
    chips.push(utility.utility_kind_other)
  }
  return chips
}

function UtilityFactoryBreadcrumb({
  site,
  line,
  currentId,
  currentName
}: {
  site?: PlantSite | null
  line?: PlantLine | null
  currentId?: string
  currentName?: string
}): React.ReactElement {
  return (
    <nav className="text-sm text-ink-3" aria-label="Breadcrumb">
      <Link to={plantEquipmentIndexHref()} className="hover:text-ink hover:underline">
        Sites
      </Link>
      {site && (
        <>
          <span className="mx-1.5 text-ink-4">/</span>
          <PlantHref
            id={site.id}
            label="Site"
            node={site}
            className="inline-flex items-center gap-1.5 hover:text-ink hover:underline"
          >
            {site.name}
            <SessionEntityMark entityId={site.id} />
          </PlantHref>
        </>
      )}
      {line && (
        <>
          <span className="mx-1.5 text-ink-4">/</span>
          <PlantHref
            id={line.id}
            label="Line"
            node={site ? { ...line, siteId: site.id } : line}
            className="inline-flex items-center gap-1.5 hover:text-ink hover:underline"
          >
            {line.name}
            <SessionEntityMark entityId={line.id} />
          </PlantHref>
        </>
      )}
      {currentName && currentId && (
        <>
          <span className="mx-1.5 text-ink-4">/</span>
          <SessionBreadcrumbCurrent entityId={currentId}>{currentName}</SessionBreadcrumbCurrent>
        </>
      )}
    </nav>
  )
}

function ConsumersSection({
  consumers
}: {
  consumers: PlantUtilityConsumer[]
}): React.ReactElement | null {
  if (consumers.length === 0) return null

  return (
    <section>
      <SectionHeader
        label="Consumers"
        subtitle={`${consumers.length} ${consumers.length === 1 ? 'consumer' : 'consumers'}`}
      />
      <div className="flex flex-col gap-3 rounded-xl border bg-card p-5 text-card-foreground shadow-sm">
        {consumers.map(({ equipment, edge }) => {
          const props = edge.props
          const basis =
            typeof props['basis_status'] === 'string' ? props['basis_status'] : undefined
          const notes = typeof props['notes'] === 'string' ? props['notes'] : undefined
          const spec = formatUtilitySpec(props)

          return (
            <div key={`${edge.source}:${edge.target}`} className="min-w-0">
              <div className="flex items-start gap-2">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
                  <PlantHref
                    id={equipment.id}
                    label="Equipment"
                    node={equipment}
                    className="text-13 font-medium text-ink no-underline hover:text-brand hover:underline"
                  >
                    {equipment.name}
                  </PlantHref>
                  {basis && (
                    <Badge variant="outline" className={cn('text-10', basisStatusClass(basis))}>
                      {basis.replaceAll('_', ' ')}
                    </Badge>
                  )}
                  {typeof edge.confidence === 'number' && (
                    <span className="font-mono text-11 tabular-nums text-ink-3">
                      {(edge.confidence <= 1 ? edge.confidence * 100 : edge.confidence).toFixed(0)}%
                    </span>
                  )}
                </div>
                <SessionEntityMark entityId={equipment.id} />
              </div>
              {spec && <div className="mt-0.5 font-mono text-12 text-ink-3">{spec}</div>}
              {notes && <div className="mt-0.5 text-12 text-ink-3">{notes}</div>}
            </div>
          )
        })}
      </div>
    </section>
  )
}
